import { useStatsigClient } from '@statsig/react-bindings'
import { ReactQuery } from 'cms-api'
import React, { useEffect, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { ROUTE } from 'shared-constants'
import styled from 'styled-components'
import Icon from 'components/Icon/Icon'
import { Box } from 'components/Layout'
import { Modal } from 'components/Modal'
import { STATSIG_EVENTS } from 'providers/statsig'
import { useUserAuth } from 'providers/userAuth'
import { MODAL_MAX_WIDTH, SPACE } from 'Theme'
import { AddToFavoritesDialog } from './AddToFavoritesDialog'
import { AddToFavoritesHintPopover } from './AddToFavoritesHintPopover'
import { AddToFavoritesSignUp } from './AddToFavoritesSignUp'

const FavoritesBadge = styled(Box)<{ isActive?: boolean }>`
  width: ${SPACE.PX_22};
  cursor: pointer;

  svg path {
    opacity: 0.2;
    fill: white;
    transition: all 0.3s ease;
  }

  &:hover svg path {
    opacity: 1;
    fill: #126554;
  }

  ${({ isActive }) =>
    isActive &&
    `
    svg path {
      opacity: 1;
      fill: #126554;
    }
  `}
`

export const AddToFavorites = ({
  variantId,
  isBookmarked = false,
  productTitle,
  onRefetch,
  withHint = false,
}: {
  variantId: number
  isBookmarked?: boolean
  productTitle: string
  onRefetch: () => void
  withHint?: boolean
}) => {
  const queryClient = useQueryClient()
  const { client: statsigClient } = useStatsigClient()
  const { isClientSignedIn, isExpertSignedIn, user } = useUserAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isEligibleForFavorites = isClientSignedIn || isExpertSignedIn

  const { mutateAsync: mutatePutFavoriteUserProductVariantBookmark } =
    ReactQuery.usePutFavoriteUserProductVariantBookmark({
      mutation: {
        onSettled: onRefetch,
      },
    })

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (isEligibleForFavorites) {
      await mutatePutFavoriteUserProductVariantBookmark({
        data: {
          isBookmarked: !isBookmarked,
          legacyProductVariantId: variantId,
        },
      })
      setIsPopoverOpen(true)

      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
      if (statsigClient) {
        if (isBookmarked) {
          statsigClient.logEvent(
            STATSIG_EVENTS.REMOVE_FROM_FAVORITES,
            user?.email,
            {
              isTradeCapable: user?.isTradeCapable ? 'true' : 'false',
              productTitle,
              variantId: variantId?.toString(),
            }
          )
        } else {
          statsigClient.logEvent(STATSIG_EVENTS.ADD_TO_FAVORITES, user?.email)
        }
      }

      timerRef.current = setTimeout(() => {
        setIsPopoverOpen(false)
        queryClient.invalidateQueries(['userFavoriteProducts'])
      }, 5000)
    } else {
      setIsOpen(true)
    }
  }

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    },
    []
  )

  return (
    <Box position="relative" zIndex={3}>
      {withHint ? (
        <AddToFavoritesHintPopover>
          <FavoritesBadge
            isActive={isBookmarked}
            role="button"
            onClick={handleToggleFavorite}
            aria-label={`${isBookmarked ? 'Remove from' : 'Add to'} favorites`}
          >
            <Icon.Heart />
          </FavoritesBadge>
        </AddToFavoritesHintPopover>
      ) : (
        <FavoritesBadge
          isActive={isBookmarked}
          role="button"
          onClick={handleToggleFavorite}
          aria-label={`${isBookmarked ? 'Remove from' : 'Add to'} favorites`}
        >
          <Icon.Heart />
        </FavoritesBadge>
      )}

      <AddToFavoritesDialog
        isOpen={isPopoverOpen}
        productTitle={productTitle}
        isBookmarked={isBookmarked}
        onClose={() => setIsPopoverOpen(false)}
        onUndo={async (e) => {
          await handleToggleFavorite(e)
        }}
        favoritesDashboardUrl={
          isClientSignedIn
            ? `${ROUTE.CLIENT_FAVORITES()}?tab=favorites`
            : `${ROUTE.EXPERT_FAVORITES()}?tab=favorites`
        }
      />

      <Modal
        ariaLabel="Sign up to save this item"
        isOpen={isOpen}
        title="Sign up to save this item"
        maxWidth={MODAL_MAX_WIDTH.PX_400}
        confirmButtonText="Delete"
        onModalClose={() => setIsOpen(false)}
        isWithFooter={false}
      >
        <AddToFavoritesSignUp setIsModalOpen={setIsOpen} />
      </Modal>
    </Box>
  )
}
